import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { API_URL } from '@/dtos';

export const GET_CONTRIBUTION_REQUEST_RECORDS_KEY = 'GET_CONTRIBUTION_REQUEST_RECORDS_KEY';




interface RecordError {
  type?: string;
  fieldName?: string;
  errorDetails?: string;
  errorCode?: string;
  createdAt?: string;
}

interface ContributionRequestRecord {
  recordId?: string;
  recordErrors?: RecordError[];
  createdAt?: string;
  processedAt?: string;
  processingStatus?: string;

  employeeId?: string;
  employeeFullName?: string;
  planCode?: string;
  planName?: string;
  memo?: string;
  contributionDate?: string;
  eeContributionTag?: string;
  erContributionTag?: string;
  employeeContributionAmount?: string;
  employerContributionAmount?: string;
  taxType?: string;
  taxYear?: string;
}

interface ContributionRequest {
  contributionId?: string;
  status?: string;
  records?: ContributionRequestRecord[];
  eeContributionHoldDays?: string;
  contributionSource?: string;
  submitDate?: string;
  requestVersion?: number;
}

const getExtraDataFromJson = (jsonData?: string) => {
  if (!jsonData) return {};

  try {
    const data = JSON.parse(jsonData);

    return {
      employeeId: toString(data?.employee_id),
      employeeFullName: data?.employee_full_name,
      planCode: data?.plan_code,
      planName: data?.plan_name,
      memo: data?.memo,
      contributionDate: data?.contribution_date,
      eeContributionTag: data?.ee_contribution_tag,
      erContributionTag: data?.er_contribution_tag,
      employeeContributionAmount: toString(data?.employee_contribution_amount),
      employerContributionAmount: toString(data?.employer_contribution_amount),
      taxType: data?.tax_type,
      taxYear: data?.tax_year,
    };
  } catch {
    return {};
  }
};

export const useGetContributionRequestRecords = (
  contributionRequestId?: string,
  hasRecordData?: boolean,
  enabled: boolean = true
) => {
  const { data, isLoading } = useQuery(
    [GET_CONTRIBUTION_REQUEST_RECORDS_KEY, contributionRequestId, hasRecordData, enabled],
    () =>
      api.get(
        API_URL.CONTRIBUTIONS_CONTRIBUTION_REQUESTS_BY_CONTRIBUTION_REQUEST_ID(contributionRequestId || ''),
        {
          params: {
            record_data: hasRecordData,
          },
        }
      ),
    {
      enabled: Boolean(contributionRequestId && enabled),
    }
  );

  const contributionRequest = useMemo<ContributionRequest>(
    () => ({
      contributionId: data?.data?.contribution_id,
      status: data?.data?.status,
      records: data?.data?.records?.map(
        (item): ContributionRequestRecord => ({
          recordId: `${item?.record_id || ''}`,
          recordErrors: item?.record_errors?.map((item) => ({
            type: item?.type,
            fieldName: item?.field_name,
            errorDetails: item?.error_details,
            errorCode: item?.error_code,
            createAt: item?.created_at,
          })),
          createdAt: item?.created_at as unknown as string,
          processedAt: item?.processed_at as unknown as string,
          processingStatus: item?.processing_status,

          ...getExtraDataFromJson(item?.json_data),
        })
      ),
      eeContributionHoldDays: `${data?.data?.ee_contribution_hold_days || ''}`,
      contributionSource: data?.data?.contribution_source,
      submitDate: data?.data?.submit_date as unknown as string,
      requestVersion: data?.data?.request_version,
    }),
    [data]
  );

  return {
    contributionRequest,
    isLoading,
  };
};
